import { post } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { getSystemListItemId } from "../Issues/issueMappers";

/**
 * Normalize GET /projects/:projectId/tags body (axios response.data).
 * Shape: { projectId, clientTags: [...], vendorTags: [...], languageTags: [...] }
 */
export function parseProjectTagsPayload(payload) {
  if (payload == null) {
    return { projectId: null, clientTags: [], vendorTags: [], languageTags: [] };
  }
  const root = payload?.data !== undefined ? payload.data : payload;
  const d =
    root?.data != null && typeof root.data === "object" && !Array.isArray(root.data)
      ? root.data
      : root;
  return {
    projectId: d?.projectId ?? d?.id ?? null,
    clientTags: Array.isArray(d?.clientTags) ? d.clientTags : [],
    vendorTags: Array.isArray(d?.vendorTags) ? d.vendorTags : [],
    languageTags: Array.isArray(d?.languageTags) ? d.languageTags : [],
  };
}

/** system_lists rows → multiselect values (tag ids). */
export function systemListRowsToTagIds(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const row of rows) {
    if (row == null) continue;
    if (typeof row === "string") {
      const s = String(row).trim();
      if (s) out.push(s);
      continue;
    }
    const id = getSystemListItemId(row) ?? row.id;
    if (id != null && String(id).trim() !== "") out.push(String(id));
  }
  return out;
}

/** system_lists rows → tag display names (for TagsInput), deduped by case-insensitive name. */
export function systemListRowsToTagNames(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  const seen = new Set();
  for (const row of rows) {
    if (row == null) continue;
    const name =
      typeof row === "string"
        ? String(row).trim()
        : String(row.name ?? row.label ?? row.displayName ?? "").trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

/** Resolve tag ids to display names using cached system list rows (fallback when GET /tags fails). */
export function tagIdsToNames(ids, listItems) {
  if (!Array.isArray(ids) || !ids.length) return [];
  const list = Array.isArray(listItems) ? listItems : [];
  const out = [];
  const seen = new Set();
  for (const rawId of ids) {
    const id = String(rawId);
    const row = list.find((x) => String(getSystemListItemId(x) ?? x.id) === id);
    const name = row ? String(row.name ?? row.label ?? row.displayName ?? "").trim() : "";
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

function normLabel(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase();
}

function findExistingTagId(listItems, name) {
  const want = normLabel(name);
  if (!want || !Array.isArray(listItems)) return null;
  for (const item of listItems) {
    if (item == null || typeof item === "string") continue;
    const label = item.name ?? item.label ?? item.displayName ?? "";
    if (normLabel(label) === want) {
      const id = getSystemListItemId(item);
      return id != null ? String(id) : null;
    }
  }
  return null;
}

/** POST /tags — id often at response.data.data.id (axios: res.data = API body) */
function extractTagIdFromCreateResponse(apiBody) {
  if (!apiBody || typeof apiBody !== "object") return null;
  const id =
    apiBody.data?.id ??
    apiBody.data?.data?.id ??
    apiBody.id ??
    (apiBody.data && typeof apiBody.data === "object" ? apiBody.data.id : null);
  return id != null ? String(id) : null;
}

/**
 * For each tag name (manual entry): reuse id from system list when names match, else POST /tags.
 * @param {string[]} tagNames
 * @param {"CLIENT_TAG"|"VENDOR_TAG"} listType
 * @param {unknown[]} existingListItems - from GET /system-lists?listType=…
 * @returns {Promise<string[]>}
 */
export async function resolveTagNamesToIds(tagNames, listType, existingListItems) {
  const orderedUnique = [];
  const seen = new Set();
  for (const raw of tagNames || []) {
    const trimmed = String(raw).trim();
    if (!trimmed) continue;
    const key = normLabel(trimmed);
    if (seen.has(key)) continue;
    seen.add(key);
    orderedUnique.push(trimmed);
  }

  const ids = [];
  /** In-memory so two new names in one submit both resolve; also covers race with same name */
  const createdByLabel = new Map();

  for (const name of orderedUnique) {
    const key = normLabel(name);
    let id = findExistingTagId(existingListItems, name) ?? createdByLabel.get(key);

    if (!id) {
      const res = await post(API_ENDPOINTS.TAGS.CREATE, {
        listType,
        name: name.trim(),
      });
      id = extractTagIdFromCreateResponse(res?.data);
      if (!id) {
        throw new Error(`Could not get id for tag "${name}".`);
      }
      createdByLabel.set(key, id);
    }
    ids.push(id);
  }
  return ids;
}
